import React, { useState, useEffect } from 'react'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material'
import {
  Assignment as StatusIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material'
import { SelectChangeEvent } from '@mui/material/Select'
import { useStatuses, useStatusStats } from '../hooks/useStatuses'

interface StatusFilterProps {
  selectedStatuses: string[]
  onStatusesChange: (statuses: string[]) => void
  disabled?: boolean
}

interface Status {
  name: string
  count?: number
}

const StatusFilter: React.FC<StatusFilterProps> = ({
  selectedStatuses,
  onStatusesChange,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  // Debounce search term to avoid immediate filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300) // 300ms delay

    return () => clearTimeout(timer)
  }, [searchTerm])

  const { data: statusesData, isLoading: statusesLoading, error: statusesError } = useStatuses()
  const { data: statsData, isLoading: statsLoading, error: statsError } = useStatusStats()

  // Combine status data with stats
  const statuses: Status[] = React.useMemo(() => {
    if (!statusesData?.data) return []
    
    const statusList = statusesData.data
    const stats = statsData?.data || {}
    
    return statusList.map(status => ({
      name: status,
      count: stats[status] || 0
    }))
  }, [statusesData, statsData])

  // Filter statuses based on debounced search term
  const filteredStatuses = statuses.filter(status =>
    status.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  )

  const handleChange = (event: SelectChangeEvent<typeof selectedStatuses>) => {
    const value = event.target.value
    onStatusesChange(typeof value === 'string' ? value.split(',') : value)
  }

  const handleChipDelete = (statusToDelete: string) => {
    onStatusesChange(selectedStatuses.filter(status => status !== statusToDelete))
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const clearSearch = () => {
    setSearchTerm('')
  }

  const clearAll = () => {
    onStatusesChange([])
  }

  const handleOpen = () => {
    setIsOpen(true)
    // Auto-focus on search input when dropdown opens
    setTimeout(() => {
      const searchInput = document.querySelector('#status-search-input') as HTMLInputElement
      if (searchInput) {
        searchInput.focus()
      }
    }, 100)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSearchTerm('') // Clear search when dropdown closes
  }

  if (statusesError) {
    return (
      <Alert severity="error" sx={{ minWidth: 200 }}>
        Failed to load statuses
      </Alert>
    )
  }

  return (
    <FormControl
      sx={{ minWidth: 180, maxWidth: 280 }} // Reduced minWidth from 200 to 180, maxWidth from 300 to 280
      size="small"
      disabled={disabled}
    >
      <InputLabel id="status-filter-label" sx={{ fontSize: '0.8rem' }}>Status</InputLabel>
      <Select
        labelId="status-filter-label"
        id="status-filter"
        multiple
        value={selectedStatuses}
        onChange={handleChange}
        open={isOpen}
        onClose={handleClose}
        onOpen={handleOpen}
        label="Status"
        disabled={disabled}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}> {/* Reduced gap from 0.5 to 0.25 */}
            {selected.map((value) => (
              <Chip
                key={value}
                label={value}
                size="small"
                onDelete={() => handleChipDelete(value)}
                onMouseDown={(event) => {
                  event.stopPropagation()
                }}
                sx={{
                  height: 20, // Slightly smaller chip height
                  fontSize: '0.75rem' // Smaller font size
                }}
              />
            ))}
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 350,
              minWidth: 'auto', // Dynamic width based on content
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
      >
        {/* Search field */}
        <MenuItem disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
          <TextField
            id="status-search-input"
            size="small"
            placeholder="Search statuses..."
            value={searchTerm}
            onChange={handleSearchChange}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              // Allow arrow keys to navigate through menu items
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                return // Don't stop propagation for arrow keys
              }
              e.stopPropagation()
            }}
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      clearSearch()
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </MenuItem>

        {/* Loading indicator */}
        {(statusesLoading || statsLoading) && (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading statuses...</Typography>
            </Box>
          </MenuItem>
        )}

        {/* Clear all option */}
        {selectedStatuses.length > 0 && (
          <MenuItem
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
              clearAll()
            }}
            sx={{
              fontStyle: 'italic',
              color: 'primary.main',
              borderBottom: '1px solid',
              borderColor: 'divider',
              mb: 1
            }}
          >
            Clear all selections
          </MenuItem>
        )}
        
        {filteredStatuses.map((status) => (
          <MenuItem key={status.name} value={status.name} sx={{ py: 0.75 }}> {/* Reduced padding from 1 to 0.75 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}> {/* Reduced gap from 1 to 0.75 */}
              <StatusIcon fontSize="small" color="primary" />
              <Typography variant="body2" sx={{ flexGrow: 1, fontSize: '0.875rem' }}> {/* Slightly smaller font */}
                {status.name}
              </Typography>
              {status.count !== undefined && (
                <Chip
                  label={status.count}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{
                    ml: 0.75, // Reduced from 1 to 0.75
                    fontSize: '0.7rem', // Reduced from 0.75rem to 0.7rem
                    height: '18px', // Reduced from 20px to 18px
                    '& .MuiChip-label': {
                      px: 0.75 // Reduced from 1 to 0.75
                    }
                  }}
                />
              )}
            </Box>
          </MenuItem>
        ))}

        {/* No results message */}
        {!statusesLoading && !statsLoading && filteredStatuses.length === 0 && searchTerm && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No statuses found matching "{searchTerm}"
            </Typography>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  )
}

export default StatusFilter
